/**
 * Android 专用 · mounted input 选图 session
 *
 * 生命周期：
 * 1. 立即挂载 `change` / `cancel` 监听
 * 2. 调用方 `clickHiddenFileInput` 打开系统选择器
 * 3. 调用方 `activateCancelDetection()` 再挂载 focus / blur / visibility
 * 4. settleFiles 完成后 resolve；abort 或超时 resolve null
 *
 * **禁止**用于 iOS 开弹窗前选图（focus 时序问题）。
 *
 * @see pickPortfolioFilesFromMountedInput
 * @see apps/token-seek/features/20260610-portfolio-file-pick-platform-strategy/SPEC.md
 */
import type { PortfolioFilePickCancelDetection } from "./portfolioPickConfig";

/** 无任何回调时的安全超时（ms） */
export const PORTFOLIO_FILE_PICK_SESSION_MAX_MS = 5 * 60 * 1000;

export type RunPortfolioFilePickSessionParams = {
  input: HTMLInputElement;
  cancelPollMs: number;
  settleFiles: (files: File[]) => Promise<File[] | null>;
  sessionMaxMs?: number;
  cancelDetection?: PortfolioFilePickCancelDetection;
};

export type PortfolioFilePickSession = {
  promise: Promise<File[] | null>;
  abort: () => void;
  /** Attach focus/blur/visibility listeners — call only after the picker is opened. */
  activateCancelDetection: () => void;
};

const DEFAULT_CANCEL_DETECTION: PortfolioFilePickCancelDetection = {
  useWindowFocus: true,
  useVisibilityChange: false,
  useInputBlur: false,
};

/**
 * 包装 mounted file input 的一次选图会话。
 * @returns promise — 选中并 settle 后的文件，或 null（取消/失败）
 * @returns activateCancelDetection — 必须在 click 打开 picker 之后调用
 */
export function runPortfolioFilePickSession({
  input,
  cancelPollMs,
  settleFiles,
  sessionMaxMs = PORTFOLIO_FILE_PICK_SESSION_MAX_MS,
  cancelDetection = DEFAULT_CANCEL_DETECTION,
}: RunPortfolioFilePickSessionParams): PortfolioFilePickSession {
  let abortFn: () => void = () => {};
  let activateCancelDetectionFn: () => void = () => {};

  const promise = new Promise<File[] | null>((resolve) => {
    let settled = false;
    let settling = false;
    let cancelDetectionActive = false;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let safetyTimer: ReturnType<typeof setTimeout> | null = null;

    const clearPollTimer = () => {
      if (pollTimer !== null) {
        clearTimeout(pollTimer);
        pollTimer = null;
      }
    };

    const cleanup = () => {
      clearPollTimer();
      if (safetyTimer !== null) {
        clearTimeout(safetyTimer);
        safetyTimer = null;
      }
      if (cancelDetectionActive) {
        if (cancelDetection.useWindowFocus) {
          window.removeEventListener("focus", onWindowFocus);
        }
        if (cancelDetection.useVisibilityChange) {
          document.removeEventListener("visibilitychange", onVisibilityChange);
        }
        if (cancelDetection.useInputBlur) {
          input.removeEventListener("blur", onInputBlur);
        }
      }
      input.removeEventListener("change", onChange);
      input.removeEventListener("cancel", onCancel);
    };

    const finish = (files: File[] | null) => {
      if (settled) {
        return;
      }
      settled = true;
      settling = false;
      cleanup();
      resolve(files);
    };

    abortFn = () => {
      finish(null);
    };

    const settleFromInput = () => {
      if (settled || settling) {
        return;
      }
      settling = true;
      void (async () => {
        try {
          const picked = Array.from(input.files || []);
          if (picked.length === 0) {
            input.value = "";
            finish(null);
            return;
          }
          const result = await settleFiles(picked);
          input.value = "";
          finish(result);
        } catch {
          settling = false;
          finish(null);
        }
      })();
    };

    const scheduleSettlePoll = () => {
      if (settled || settling) {
        return;
      }
      clearPollTimer();
      pollTimer = setTimeout(() => {
        pollTimer = null;
        if (!settled) {
          settleFromInput();
        }
      }, cancelPollMs);
    };

    const onChange = () => {
      settleFromInput();
    };

    const onCancel = () => {
      finish(null);
    };

    const onWindowFocus = () => {
      scheduleSettlePoll();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        scheduleSettlePoll();
      }
    };

    const onInputBlur = () => {
      scheduleSettlePoll();
    };

    activateCancelDetectionFn = () => {
      if (cancelDetectionActive || settled) {
        return;
      }
      cancelDetectionActive = true;
      if (cancelDetection.useInputBlur) {
        input.addEventListener("blur", onInputBlur);
      }
      if (cancelDetection.useWindowFocus) {
        window.addEventListener("focus", onWindowFocus);
      }
      if (cancelDetection.useVisibilityChange) {
        document.addEventListener("visibilitychange", onVisibilityChange);
      }
    };

    input.addEventListener("change", onChange);
    input.addEventListener("cancel", onCancel);

    safetyTimer = setTimeout(() => {
      finish(null);
    }, sessionMaxMs);
  });

  return {
    promise,
    abort: () => {
      abortFn();
    },
    activateCancelDetection: () => {
      activateCancelDetectionFn();
    },
  };
}
